import { RiskFactorsSubCodes } from "@/Common/Resources/Lookups/Enums/RiskFactorLookupCodes";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { RiskFactorCategoryCode, RiskFactorCode } from "@/Patient/Models/MedicalSummary/RiskFactorCode";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { CareElementType } from "../Models/CareElements/CareElementType";
import { RiskFactorCareElement } from "../Models/CareElements/RiskFactorCareElement";
import { RiskFactorStatus } from "../Models/CareElements/RiskFactorStatus";
import { ICareComponentService } from "./ICareComponentService";

class FlowsheetRiskFactorService implements ICareComponentService {
    public processCareComponent(careComponent: CareComponent, patientContactId: string, encounterDate: Date): void {
        if (!encounterDate) {
            console.error("Encounter date is not defined in FlowsheetRiskFactorService");
            return;
        }

        this.processRiskFactorCareElement(
            careComponent,
            patientContactId,
            encounterDate,
            FlowsheetConstants.RiskFactors.SmokingStatusCareElementName,
            FlowsheetRiskFactorService.getSmokingRiskFactorCareElement
        );

        this.processRiskFactorCareElement(
            careComponent,
            patientContactId,
            encounterDate,
            FlowsheetConstants.RiskFactors.AlcoholUseCareElementName,
            FlowsheetRiskFactorService.getAlcoholRiskFactorCareElement
        );
    }

    private processRiskFactorCareElement(
        careComponent: CareComponent,
        patientContactId: string,
        encounterDate: Date,
        careElementName: string,
        careElementBuilder: (items: Partial<MedicalItemRiskFactor>[]) => RiskFactorCareElement
    ): void {
        const targetElement = careComponent?.CareElements.firstOrDefault(
            p => p.CareElementType === CareElementType.RiskFactor && p.Name === careElementName
        );

        if (!targetElement || targetElement.CareElementType !== CareElementType.RiskFactor) {
            return;
        }

        const riskFactors = FlowsheetRiskFactorService.getRiskFactors(patientContactId, encounterDate, careElementName);
        const filteredElement = careElementBuilder(riskFactors);

        targetElement.Status = filteredElement.Status;
        targetElement.ReportingPeriod = filteredElement.ReportingPeriod;
        targetElement.RiskFactorCodeId = filteredElement.RiskFactorCodeId;
        targetElement.Details = filteredElement.Details;
    }

    public static getRiskFactors(patientContactId: string, encounterDate: Date, careElementName: string): MedicalItemRiskFactor[] {
        const encounterDateTime = LuxonDateTimeParser.parse(encounterDate).endOf("day");

        const relevantLookups: string[] = cacheStore.RiskFactorCodeCache[
            careElementName === FlowsheetConstants.RiskFactors.SmokingStatusCareElementName
                ? RiskFactorCategoryCode.Tobacco
                : RiskFactorCategoryCode.Alcohol
        ].map(p => p.Id);

        const encounterIds = patientStore.PatientMedicalSummaryModels[patientContactId]
            ?.get("Encounters")
            .filter(p => LuxonDateTimeParser.parse(p.ConsultationDate) <= encounterDateTime)
            .map(p => p.Id)
            .toArray();

        const riskFactorsByEmrTemplateId = patientStore.PatientMedicalSummaryModels[patientContactId]
            ?.get("MedicalItemsRiskFactor")
            .filter(p => p.EMRTemplateMedicalItemType === MedicalItemTypeEnum.SocialHabitsAndRiskFactors && encounterIds.includes(p.RelatedEncounterId))
            .toArray()
            .toLookup(p => p.EMRTemplateId);

        const riskFactors: MedicalItemRiskFactor[] = Object.keys(riskFactorsByEmrTemplateId)
            .map(key => MedicalItemModel.sortByCreationDateDesc(riskFactorsByEmrTemplateId[key]).firstOrDefault())
            .filter(p => p != null && p.EMRTemplateIsInvalid !== true);

        // eslint-disable-next-line arrow-body-style
        return riskFactors.filter(p => {
            return relevantLookups.includes(p.RiskFactorCodeId)
                && (!p.EndDate || LuxonDateTimeParser.parse(p.EndDate).startOf("day") <= encounterDateTime)
                && (!p.LifeStageDate || (LuxonDateTimeParser.parse(p.LifeStageDate).startOf("day") <= encounterDateTime))
                && LuxonDateTimeParser.parse(p.ConsultationDate).startOf("day") <= encounterDateTime;
        });
    }

    public static resolveDate(riskFactor: Partial<MedicalItemRiskFactor>): Date | null {
        if (riskFactor.LifeStageDate && riskFactor.EndDate) {
            return riskFactor.EndDate;
        }
        else if (riskFactor.EndDate) {
            return riskFactor.EndDate;
        }
        else if (riskFactor.LifeStageDate) {
            return riskFactor.LifeStageDate;
        }
        else {
            return null;
        }
    }

    public static getSmokingRiskFactorCareElement(items: Partial<MedicalItemRiskFactor>[]): RiskFactorCareElement {
        const { nonFrequencySpecificIds, smokerLookUpsIds, nonSmokerId } = getTobaccoRiskFactorLookups();

        return FlowsheetRiskFactorService.getRiskFactorCareElementBase(items, {
            nonFrequencySpecificIds,
            positiveIds: smokerLookUpsIds,
            negativeId: nonSmokerId
        });
    }

    public static getAlcoholRiskFactorCareElement(items: Partial<MedicalItemRiskFactor>[]): RiskFactorCareElement {
        const { nonFrequencySpecificIds, alcoholLookUpsIds, nonAlcoholId } = getAlcoholRiskFactorLookups();

        return FlowsheetRiskFactorService.getRiskFactorCareElementBase(items, {
            nonFrequencySpecificIds,
            positiveIds: alcoholLookUpsIds,
            negativeId: nonAlcoholId
        });
    }

    private static getRiskFactorCareElementBase(
        items: Partial<MedicalItemRiskFactor>[],
        options: {
            nonFrequencySpecificIds: string[],
            positiveIds: string[],
            negativeId: string
        }
    ): RiskFactorCareElement {
        if (!items?.length) return new RiskFactorCareElement({});

        const { nonFrequencySpecificIds, positiveIds, negativeId } = options;

        if (items.length === 1 && items[0].RiskFactorCodeId === negativeId) {
            return new RiskFactorCareElement({
                Status: RiskFactorStatus.Negative,
                ReportingPeriod: {
                    StartDate: this.resolveDate(items[0]),
                    EndDate: null
                },
                RiskFactorCodeId: items[0].RiskFactorCodeId,
                Details: items[0].Details
            });
        }

        if (items.length === 1 && positiveIds.includes(items[0].RiskFactorCodeId)) {
            const riskFactor = new RiskFactorCareElement({
                Status: RiskFactorStatus.Positive,
                ReportingPeriod: {
                    StartDate: items[0].LifeStageDate ?? null,
                    EndDate: items[0].LifeStageDate ?? null
                },
                RiskFactorCodeId: items[0].RiskFactorCodeId,
                Details: items[0].Details
            });

            if (nonFrequencySpecificIds.includes(items[0].RiskFactorCodeId)) {
                riskFactor.ReportingPeriod.EndDate = null;
            }

            return riskFactor;
        }

        const itemsWithDates = items
            .filter(p => !!p.LifeStageDate || !!p.EndDate)
            .sortByPropertyDesc(p => p.LifeStageDate ?? p.EndDate);

        if (itemsWithDates.length === 0) {
            if (items.every(p => p.RiskFactorCodeId === negativeId)) {
                return new RiskFactorCareElement({
                    Status: RiskFactorStatus.Negative,
                    ReportingPeriod: null
                });
            }
            else if (items.some(p => positiveIds.includes(p.RiskFactorCodeId))) {
                return new RiskFactorCareElement({
                    Status: RiskFactorStatus.Positive,
                    ReportingPeriod: null
                });
            }
        }

        const firstItem = itemsWithDates.firstOrDefault();
        const lastItem = itemsWithDates[itemsWithDates.length - 1];

        if (firstItem.RiskFactorCodeId === negativeId) {
            return new RiskFactorCareElement({
                Status: RiskFactorStatus.Negative,
                ReportingPeriod: {
                    StartDate: this.resolveDate(firstItem),
                    EndDate: null
                },
                RiskFactorCodeId: firstItem.RiskFactorCodeId,
                Details: firstItem.Details
            });
        }

        if (positiveIds.includes(firstItem.RiskFactorCodeId) && !nonFrequencySpecificIds.includes(firstItem.RiskFactorCodeId)) {
            return new RiskFactorCareElement({
                Status: RiskFactorStatus.Positive,
                ReportingPeriod: {
                    StartDate: this.resolveDate(lastItem),
                    EndDate: this.resolveDate(firstItem)
                },
                RiskFactorCodeId: firstItem.RiskFactorCodeId,
                Details: firstItem.Details
            });
        }

        if (nonFrequencySpecificIds.includes(firstItem.RiskFactorCodeId)) {
            return new RiskFactorCareElement({
                Status: RiskFactorStatus.Positive,
                ReportingPeriod: {
                    StartDate: lastItem.LifeStageDate ?? lastItem.EndDate ?? null,
                    EndDate: null
                },
                RiskFactorCodeId: firstItem.RiskFactorCodeId,
                Details: firstItem.Details
            });
        }

        return new RiskFactorCareElement({
            Status: RiskFactorStatus.Unknown
        });
    }

    private static refreshRiskFactorCareElement(
        careComponent: CareComponent,
        patientContactId: string,
        encounterDate: Date,
        careElementName: string,
        getFilteredCareElementFn: (riskFactors: any) => RiskFactorCareElement
    ): RiskFactorCareElement {
        const careElement = careComponent
            ?.CareElements
            .firstOrDefault(p => p.CareElementType === CareElementType.RiskFactor && p.Name == careElementName);

        if (careElement && careElement.CareElementType === CareElementType.RiskFactor) {
            const riskFactors = FlowsheetRiskFactorService.getRiskFactors(patientContactId, encounterDate, careElementName);

            const filteredCareElement = getFilteredCareElementFn(riskFactors);

            careElement.Frequency = filteredCareElement.Frequency;
            careElement.Status = filteredCareElement.Status;
            careElement.RiskFactorCodeId = filteredCareElement.RiskFactorCodeId;
            careElement.ReportingPeriod = filteredCareElement.ReportingPeriod;
            careElement.Details = filteredCareElement.Details;

            return new RiskFactorCareElement(careElement);
        }

        return null;
    }

    public static refreshSmokingCareElement(careComponent: CareComponent, patientContactId: string, encounterDate: Date): RiskFactorCareElement {
        return FlowsheetRiskFactorService.refreshRiskFactorCareElement(
            careComponent,
            patientContactId,
            encounterDate,
            FlowsheetConstants.RiskFactors.SmokingStatusCareElementName,
            FlowsheetRiskFactorService.getSmokingRiskFactorCareElement
        );
    }

    public static refreshAlcoholCareElement(careComponent: CareComponent, patientContactId: string, encounterDate: Date): RiskFactorCareElement {
        return FlowsheetRiskFactorService.refreshRiskFactorCareElement(
            careComponent,
            patientContactId,
            encounterDate,
            FlowsheetConstants.RiskFactors.AlcoholUseCareElementName,
            FlowsheetRiskFactorService.getAlcoholRiskFactorCareElement
        );
    }
}

function getTobaccoRiskFactorLookups(): {
    nonFrequencySpecificIds: string[];
    smokerLookUpsIds: string[];
    nonSmokerId: string;
} {
    const { positiveIds, negativeId, nonFrequencySpecificIds } = getRiskFactorLookups(
        RiskFactorCategoryCode.Tobacco,
        RiskFactorsSubCodes.Tobacco.NonSmoker,
        [
            RiskFactorsSubCodes.Tobacco.Pipe,
            RiskFactorsSubCodes.Tobacco.Shisha,
            RiskFactorsSubCodes.Tobacco.Occasional
        ]
    );

    return {
        smokerLookUpsIds: positiveIds,
        nonSmokerId: negativeId,
        nonFrequencySpecificIds
    };
}

function getAlcoholRiskFactorLookups(): {
    nonFrequencySpecificIds: string[];
    alcoholLookUpsIds: string[];
    nonAlcoholId: string;
} {
    const { positiveIds, negativeId, nonFrequencySpecificIds } = getRiskFactorLookups(
        RiskFactorCategoryCode.Alcohol,
        RiskFactorsSubCodes.Alcohol.NoAlcohol,
        [RiskFactorsSubCodes.Alcohol.Occasional, RiskFactorsSubCodes.Alcohol.DrinksPerWeek]
    );

    return {
        alcoholLookUpsIds: positiveIds,
        nonAlcoholId: negativeId,
        nonFrequencySpecificIds
    };
}

function getRiskFactorLookups(
    category: RiskFactorCategoryCode,
    negativeCode: string,
    nonFrequencySpecificCodes: string[]
): {
        nonFrequencySpecificIds: string[];
        positiveIds: string[];
        negativeId: string;
    } {
    const lookups: RiskFactorCode[] = cacheStore.RiskFactorCodeCache[category];
    const positiveLookups = lookups.filter(p => p.Code !== negativeCode);

    return {
        positiveIds: positiveLookups.map(p => p.Id),
        negativeId: lookups.firstOrDefault(p => p.Code === negativeCode)?.Id,
        nonFrequencySpecificIds: lookups
            .filter(p => nonFrequencySpecificCodes.includes(p.Code))
            .map(p => p.Id)
    };
}

export { FlowsheetRiskFactorService };
